// Typed fetch wrapper for the dev/test gao-id-worker API.
//
// Hard guarantees:
//   * Every request goes to `API_BASE` (resolved by src/config.ts);
//     production hosts are rejected at module load.
//   * The bearer token is read from the in-memory session store
//     (src/auth/session.ts) — NEVER from localStorage or any other
//     persistent surface that could leak across browser sessions.
//   * The wrapper distinguishes between "expected refusal"
//     (401 / 403 / 400 / 409 / 503) and "unexpected" (404 / 5xx
//     other than 503). A 404 ALWAYS surfaces as a `RouteNotFoundError`
//     so a regression in BE route registration is loud, not
//     silent-failed.
//   * No response body content is logged. The caller decides
//     whether to surface a sanitised error message.

import { API_BASE } from "../config";
import { getSessionToken, markSessionExpired } from "../auth/session";

export class RouteNotFoundError extends Error {
  constructor(public readonly path: string) {
    super(`Route ${path} returned 404. The BE route registration may have regressed.`);
  }
}

export class ApiHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly path: string,
  ) {
    const code =
      body !== null && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : "unknown_error";
    super(`HTTP ${status} ${code} from ${path}`);
  }
}

export interface ApiCallOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | undefined>;
  /** When true, a 401 surfaces as `null` instead of throwing. The
   *  caller (e.g. the auth bootstrap) uses this for soft-probes. */
  treat401AsNull?: boolean;
  /** Inject a bearer token for tests / overrides. When unset, the
   *  module reads from `getSessionToken()`. */
  bearer?: string | null;
  /** Inject a custom fetch implementation for tests. */
  fetcher?: typeof fetch;
  /**
   * Treat a 404 as a RESOURCE answer rather than a routing regression.
   *
   * The default — every caller that does not set this — is unchanged: a 404
   * throws `RouteNotFoundError`, which is what makes a lost BE route loud
   * instead of silently empty.
   *
   * That default is wrong for a family whose 404 is a real answer. The
   * Control Plane BFF returns `404 {error:"not_found"}` for "unknown
   * product", "unknown revision", "product is not available" and "no
   * published revision for this product" — four distinct operator-facing
   * facts that the default collapses into "the BE route registration may
   * have regressed". Opting in lets those reach the caller as an
   * `ApiHttpError` carrying the server's own envelope.
   *
   * The regression signal is NOT lost: a genuinely missing route answers 404
   * with no `{error:"not_found"}` body, and `classifyControlPlaneError`
   * reports that case as `route_missing`.
   */
  resourceNotFound?: boolean;
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  if (!path.startsWith("/")) {
    throw new Error(`API path must start with "/": got ${path}`);
  }
  const url = new URL(API_BASE + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (typeof v === "string" && v !== "") {
        url.searchParams.set(k, v);
      }
    }
  }
  return url.toString();
}

export async function apiCall<T = unknown>(
  path: string,
  opts: ApiCallOptions = {},
): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const bearer =
    opts.bearer === null
      ? null
      : opts.bearer !== undefined
        ? opts.bearer
        : getSessionToken();
  if (bearer !== null) {
    headers.authorization = `Bearer ${bearer}`;
  }
  const url = buildUrl(path, opts.query);
  const f = opts.fetcher ?? fetch;
  const res = await f(url, { method, headers, body, credentials: "omit" });
  if (res.status === 404 && opts.resourceNotFound !== true) {
    throw new RouteNotFoundError(path);
  }
  // When a request that carried a bearer header is rejected with
  // 401, the BE has either expired the session, revoked the wallet,
  // restarted the Worker (sessions are D1-backed but `key_secret`
  // verification could fail under D1 outage), or refused for a
  // similar session-state reason. Clear the stored session and set
  // the one-shot expired flag so the AuthRequired guard surfaces a
  // clear "session expired" notice. We deliberately do NOT clear
  // for pre-auth routes (`bearer === null`) — those 401s mean the
  // caller's credentials are wrong, not that an existing session
  // expired.
  const bearerWasAttached = bearer !== null && bearer !== undefined;
  if (res.status === 401 && bearerWasAttached) {
    // Pass the rejected route so the safe debug bundle can record
    // *which* BFF endpoint refused the bearer — material context
    // for diagnosing whether the BE minted a token its own /session
    // route doesn't accept.
    markSessionExpired(path);
  }
  if (res.status === 401 && opts.treat401AsNull) {
    return null as unknown as T;
  }
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text === "" ? null : JSON.parse(text);
  } catch {
    parsed = text;
  }
  if (res.status >= 400) {
    throw new ApiHttpError(res.status, parsed, path);
  }
  return parsed as T;
}

export interface ContractsConfigResponse {
  chainId: number;
  network: string;
  requiredConfirmations: number;
  paymentIntentTtlMinutes: number;
  token: { symbol: string; address: string; decimals: number };
  escrow: { address: string; abiVersion: string };
}

export interface ContractsHealthResponse {
  chainId: number;
  network: string;
  healthy: boolean;
  reason: string | null;
  message: string | null;
  contracts: {
    escrow: {
      address: string;
      hasBytecode: boolean;
      bytecodeLength: number;
      healthy: boolean;
      abiVersion: string;
      detectedFunctions: string[];
    };
    usdc: {
      address: string;
      hasBytecode: boolean;
      bytecodeLength: number;
      healthy: boolean;
      decimals: number;
      symbol: string;
    };
  };
  rpc: {
    configured: boolean;
    reachable: boolean;
    latestBlock: number;
    lastError: string | null;
  };
}

export interface EscrowBucketsResponse {
  escrow: {
    address: string;
    chainId: number;
    network: string;
    abiVersion: string;
  };
  token: { address: string; symbol: string; decimals: number };
  buckets: {
    treasuryWithdrawable: string;
    affiliateWithdrawable: string | null;
    affiliate: string | null;
  };
}

export interface WithdrawSuccessResponse {
  ok: true;
  txHash: string;
  blockNumber: number;
  amount: string;
}

// ── Admin owner login envelope shapes ───────────────────────────────
//
// These match `gao-id-worker/src/routes/admin-owner-auth.ts` on
// `main` SHA `f01bf6c` byte-for-byte. The /challenge endpoint
// REQUIRES all four fields below; sending fewer returns
// `400 bad_request "invalid challenge request"`.

export interface ChallengeRequestBody {
  ownerSubject: string;
  walletAddress: string;
  chainId: number;
  audience: string;
}

export interface ChallengeResponseBody {
  ok: true;
  requestId: string;
  issuedAt: string;
  expiresAt: string;
  typedData: {
    domain: { name: string; version: string; chainId: number };
    types: {
      AdminLogin: ReadonlyArray<{ name: string; type: string }>;
    };
    primaryType: "AdminLogin";
    message: {
      audience: string;
      nonce: string;
      issuedAt: string;
      expiresAt: string;
      requestId: string;
      ownerSubject: string;
    };
  };
}

export interface VerifyRequestBody {
  requestId: string;
  walletAddress: string;
  signature: string;
  walletType?: "eoa" | "erc1271";
  deviceLabel?: string;
  deviceFingerprintHash?: string;
}

export interface VerifyResponseBody {
  ok: true;
  sessionToken: string;
  /** Some BE versions might wrap; the SPA's extractor in Login.tsx
   *  handles both shapes. */
  session?: { token?: string };
  expiresAt?: string;
  [k: string]: unknown;
}

/** Public address shape — 0x + 40 lowercase hex chars. */
export type EvmAddress = `0x${string}`;
